import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import OrganizationSettings from './OrganizationSettings'
import HiringPrinciplesSettings from './HiringPrinciplesSettings'
import RoleLevelSettings from './RoleLevelSettings'
import EvaluationRubricsSettings from './EvaluationRubricsSettings'
import IntegrationsSettings from './IntegrationsSettings'
import AuditComplianceSettings from './AuditComplianceSettings'

// Navigation items for settings
const settingsNav = [
  {
    id: 'organization',
    name: 'Organization',
    description: 'Company identity and region',
    icon: BuildingIcon,
  },
  {
    id: 'principles',
    name: 'Hiring Principles',
    description: 'Values that guide every hire',
    icon: ScaleIcon,
  },
  {
    id: 'levels',
    name: 'Role & Level Framework',
    description: 'Job families and expectations',
    icon: ChartBarIcon,
  },
  {
    id: 'rubrics',
    name: 'Evaluation Rubrics',
    description: 'How candidates are assessed',
    icon: ClipboardDocumentCheckIcon,
  },
  {
    id: 'integrations',
    name: 'Integrations',
    description: 'Connected tools and services',
    icon: PuzzlePieceIcon,
  },
  {
    id: 'audit',
    name: 'Audit & Compliance',
    description: 'History and accountability',
    icon: ShieldCheckIcon,
  },
]

// Icons
function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
    </svg>
  )
}

function ScaleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0012 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 01-2.031.352 5.988 5.988 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 01-2.031.352 5.989 5.989 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971z" />
    </svg>
  )
}

function ChartBarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  )
}

function ClipboardDocumentCheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0118 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3l1.5 1.5 3-3.75" />
    </svg>
  )
}

function PuzzlePieceIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.25 6.087c0-.355.186-.676.401-.959.221-.29.349-.634.349-1.003 0-1.036-1.007-1.875-2.25-1.875s-2.25.84-2.25 1.875c0 .369.128.713.349 1.003.215.283.401.604.401.959v0a.64.64 0 01-.657.643 48.39 48.39 0 01-4.163-.3c.186 1.613.293 3.25.315 4.907a.656.656 0 01-.658.663v0c-.355 0-.676-.186-.959-.401a1.647 1.647 0 00-1.003-.349c-1.036 0-1.875 1.007-1.875 2.25s.84 2.25 1.875 2.25c.369 0 .713-.128 1.003-.349.283-.215.604-.401.959-.401v0c.31 0 .555.26.532.57a48.039 48.039 0 01-.642 5.056c1.518.19 3.058.309 4.616.354a.64.64 0 00.657-.643v0c0-.355-.186-.676-.401-.959a1.647 1.647 0 01-.349-1.003c0-1.035 1.008-1.875 2.25-1.875 1.243 0 2.25.84 2.25 1.875 0 .369-.128.713-.349 1.003-.215.283-.4.604-.4.959v0c0 .333.277.599.61.58a48.1 48.1 0 005.427-.63 48.05 48.05 0 00.582-4.717.532.532 0 00-.533-.57v0c-.355 0-.676.186-.959.401-.29.221-.634.349-1.003.349-1.035 0-1.875-1.007-1.875-2.25s.84-2.25 1.875-2.25c.37 0 .713.128 1.003.349.283.215.604.401.96.401v0a.656.656 0 00.658-.663 48.422 48.422 0 00-.37-5.36c-1.886.342-3.81.574-5.766.689a.578.578 0 01-.61-.58v0z" />
    </svg>
  )
}

function ShieldCheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  )
}

function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
    </svg>
  )
}

export default function SettingsPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()
  
  // Determine active section from URL hash or default to 'organization'
  const [activeSection, setActiveSection] = useState(() => {
    const hash = location.hash.replace('#', '')
    return settingsNav.find(item => item.id === hash)?.id || 'organization'
  })

  // Update hash when section changes
  useEffect(() => {
    if (location.hash.replace('#', '') !== activeSection) {
      navigate(`/settings#${activeSection}`, { replace: true })
    }
  }, [activeSection, navigate, location.hash])

  // Listen for hash changes
  useEffect(() => {
    const hash = location.hash.replace('#', '')
    if (hash && settingsNav.find(item => item.id === hash)) {
      setActiveSection(hash)
    }
  }, [location.hash])

  const renderContent = () => {
    switch (activeSection) {
      case 'organization':
        return <OrganizationSettings />
      case 'principles':
        return <HiringPrinciplesSettings />
      case 'levels':
        return <RoleLevelSettings />
      case 'rubrics':
        return <EvaluationRubricsSettings />
      case 'integrations':
        return <IntegrationsSettings />
      case 'audit':
        return <AuditComplianceSettings />
      default:
        return <OrganizationSettings />
    }
  }

  return (
    <div className="min-h-screen bg-surface-50">
      {/* Header */}
      <header className="bg-white border-b border-surface-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Back to Dashboard */}
            <Link 
              to="/dashboard" 
              className="flex items-center gap-2 text-surface-600 hover:text-surface-900 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              <span className="font-medium">Back to Dashboard</span>
            </Link>

            {/* Logo - centered */}
            <Link to="/" className="flex items-center gap-2">
              <img src="/images/logo.png" alt="JumpstoneHR" className="w-8 h-8" />
              <span className="font-semibold text-xl text-brand-600">JumpstoneHR</span>
            </Link>

            {/* User info */}
            <div className="flex items-center gap-2 text-surface-600">
              <div className="w-8 h-8 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center font-medium text-sm">
                {user?.email?.charAt(0).toUpperCase() || 'U'}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page header */}
        <div className="mb-8">
          <h1 className="text-2xl font-display font-semibold text-surface-900">Settings</h1>
          <p className="mt-1 text-surface-600">
            Define the standards and guardrails that shape every hiring decision.
          </p>
        </div>

        <div className="flex gap-8">
          {/* Left sidebar navigation */}
          <nav className="w-72 shrink-0">
            <div className="sticky top-8 space-y-1">
              {settingsNav.map((item) => {
                const isActive = activeSection === item.id
                const Icon = item.icon
                
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveSection(item.id)}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 group ${
                      isActive 
                        ? 'bg-brand-50 border border-brand-200' 
                        : 'hover:bg-surface-100 border border-transparent'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 p-1.5 rounded-lg ${
                        isActive 
                          ? 'bg-brand-100 text-brand-700' 
                          : 'bg-surface-100 text-surface-500 group-hover:bg-surface-200'
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <p className={`font-medium ${
                          isActive ? 'text-brand-900' : 'text-surface-900'
                        }`}>
                          {item.name}
                        </p>
                        <p className={`text-sm mt-0.5 ${
                          isActive ? 'text-brand-600' : 'text-surface-500'
                        }`}>
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </nav>

          {/* Content area */}
          <main className="flex-1 min-w-0">
            <div className="bg-white rounded-2xl border border-surface-200 shadow-sm">
              {renderContent()}
            </div>
          </main>
        </div>
      </div>
    </div>
  )
}

